import type { ReactNode } from 'react';
import type { FeatureFlag } from '@gonfalon/flags/types';
import { CopyToClipboard } from '@gonfalon/launchpad-experimental/CopyToClipboard';
import { predicateFor } from '@gonfalon/legacy-form-validation/validationUtils';
import { Button, Code } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { AccessToken } from '#utils/accessTokenUtils';
import type { ExperimentApprovalResource } from '#utils/approvals/types';
import type { Environment } from '#utils/environments/records/Environment';
import type { Flag } from '#utils/flags/records/Flag';
import type { Project } from '#utils/projects/records/Project';
import type { RelayAutoConfig } from '#utils/relayAutoConfigUtils';
import type { Segment } from '#utils/segment/records/Segment';

import type { AiConfig } from './aiConfigUtils';

export const isResourceConfirmationValid = ({ input, name, key }: { input: string; name?: string; key?: string }) => {
  const s = input.trim().toLowerCase();
  const trimmedName = name?.trim().toLowerCase();
  const trimmedKey = key?.trim().toLowerCase();
  return s === trimmedName || s === trimmedKey;
};

export type ValidationEntity =
  | Environment
  | Project
  | Flag
  | Segment
  | AiConfig
  | AccessToken
  | RelayAutoConfig
  | {
      name: string;
    }
  | {
      key: string;
      name?: string;
    };

export const validation = (
  type: string,
  entity: ValidationEntity,
  label?: ReactNode,
): { label: ReactNode; placeholder: string; onValidate: (input: string) => boolean } => {
  const hasKey = 'key' in entity;
  const placeholder = hasKey ? entity.key : entity.name;
  return {
    label: label || (
      <>
        Type{' '}
        <CopyToClipboard text={placeholder}>
          <Button size="small">
            {hasKey ? <Code style={{ backgroundColor: 'transparent', padding: 0 }}>{entity.key}</Code> : entity.name}
            <Icon name="copy-clipboard" size="small" />
          </Button>
        </CopyToClipboard>{' '}
        to confirm changes to this {type}.
      </>
    ),
    placeholder,
    onValidate: (input: string) =>
      isResourceConfirmationValid({ input, name: entity.name, key: hasKey ? entity.key : undefined }),
  };
};

export const environmentValidation = (environment: Environment) => {
  return validation('environment', environment);
};

export const flagValidation = (flag: Flag) => {
  return validation('flag', flag);
};

export const featureFlagValidation = (featureFlag: Pick<FeatureFlag, 'name' | 'key'>) => {
  return validation('flag', featureFlag);
};

export const segmentValidation = (segment: Segment) => {
  return validation('segment', segment);
};

export const aiConfigValiation = (aiConfig: AiConfig) => {
  return validation('config', aiConfig);
};

export const experimentValidation = (experiment: ExperimentApprovalResource) => {
  return validation('experiment', experiment);
};

export const accessTokenValidation = (accessToken: AccessToken) => {
  return validation('access token', accessToken);
};

export const sdkKeyValidation = (sdkKeyKey: string) => {
  return validation('key', { key: sdkKeyKey });
};

export const revokeValidation = () => ({
  label: 'Type yes to confirm',
  placeholder: 'yes',
  onValidate: (input: string) => input.toLowerCase() === 'yes',
});

export const racValidation = (rac: RelayAutoConfig) => {
  return validation('Relay configuration', rac);
};

export const isValidResourceConfirmation = (key?: string, name?: string) =>
  predicateFor(
    (input: string) => !isResourceConfirmationValid({ input, key, name }),
    () => 'The key entered is incorrect',
  );
